/**
 * フォーム機能（forms）の hooks。
 * 仕様書: docs/01_spec/spec_line_bot.md A-12
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { safeAddDoc, safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';

import type {
  FieldFormElement,
  Form,
  FormElement,
  FormFieldType,
  FormReplyStatusInput,
  FormSubmission,
} from '@goenhub/shared';

import { db, functions } from '@/lib/firebase';

export interface FormRecord extends Form {
  id: string;
}

export interface FormSubmissionRecord extends FormSubmission {
  id: string;
}

/** 旧 fields[] を新 elements[] に変換するレガシーマイグレーション。 */
function migrateLegacyFields(raw: unknown): FormElement[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((f: unknown): FieldFormElement => {
    const obj = (f as Record<string, unknown>) ?? {};
    return {
      id: typeof obj.id === 'string' ? obj.id : `f_${Math.random().toString(36).slice(2, 8)}`,
      kind: 'field',
      fieldType: (obj.type as FormFieldType) ?? 'text',
      label: typeof obj.label === 'string' ? obj.label : '',
      placeholder: typeof obj.placeholder === 'string' ? obj.placeholder : undefined,
      required: !!obj.required,
      options: Array.isArray(obj.options) ? (obj.options as string[]) : undefined,
      helpText: typeof obj.helpText === 'string' ? obj.helpText : undefined,
      consentText: typeof obj.consentText === 'string' ? obj.consentText : undefined,
    };
  });
}

/** 完了通知設定を正規化。未設定 / 不正なら null。 */
function normalizeCompletionNotification(
  raw: unknown,
): Form['completionNotification'] {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    enabled: o.enabled === true,
    fromAccountId:
      typeof o.fromAccountId === 'string' && o.fromAccountId
        ? o.fromAccountId
        : null,
    cc: Array.isArray(o.cc)
      ? (o.cc as unknown[]).filter((x): x is string => typeof x === 'string')
      : [],
    subject: typeof o.subject === 'string' ? o.subject : '',
    body: typeof o.body === 'string' ? o.body : '',
    url: typeof o.url === 'string' && o.url ? o.url : null,
    includedFieldIds: Array.isArray(o.includedFieldIds)
      ? (o.includedFieldIds as unknown[]).filter(
          (x): x is string => typeof x === 'string',
        )
      : null,
  };
}

/** Firestore から取得した form を正規化（elements / 各 array フィールドのデフォルト保証）。 */
function normalizeForm(raw: Record<string, unknown>): Form {
  const elements: FormElement[] = Array.isArray(raw.elements)
    ? (raw.elements as FormElement[])
    : migrateLegacyFields(raw.fields);
  const rawTag = raw.tag;
  const tag: Form['tag'] =
    rawTag === 'sales' || rawTag === 'marketing' ? rawTag : undefined;
  const rawHint = raw.dealStageHint;
  const dealStageHint: Form['dealStageHint'] =
    rawHint === 'document_requested' || rawHint === 'briefing' || rawHint === 'quote'
      ? rawHint
      : null;
  const rawCategory = raw.category;
  const category: Form['category'] =
    rawCategory === 'document_download' ||
    rawCategory === 'explanation' ||
    rawCategory === 'quote' ||
    rawCategory === 'customer_info'
      ? rawCategory
      : null;
  return {
    name: typeof raw.name === 'string' ? raw.name : '無題のフォーム',
    title: typeof raw.title === 'string' ? raw.title : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    elements,
    thanksMessage:
      typeof raw.thanksMessage === 'string' ? raw.thanksMessage : 'ご回答ありがとうございました。',
    redirectUrl: typeof raw.redirectUrl === 'string' ? raw.redirectUrl : '',
    theme: (raw.theme as Form['theme']) ?? null,
    status: (raw.status as Form['status']) ?? 'draft',
    publicStartAt: (raw.publicStartAt as Form['publicStartAt']) ?? null,
    publicEndAt: (raw.publicEndAt as Form['publicEndAt']) ?? null,
    campaignIds: Array.isArray(raw.campaignIds) ? (raw.campaignIds as string[]) : [],
    contactTags: Array.isArray(raw.contactTags) ? (raw.contactTags as string[]) : [],
    // 顧客情報の更新ポリシー (2026-07-28)。未設定は安全側の 'fill'。
    contactOverwritePolicy:
      raw.contactOverwritePolicy === 'overwrite' ? 'overwrite' : 'fill',
    submissionCount: typeof raw.submissionCount === 'number' ? raw.submissionCount : 0,
    // 定員設定 (2026-05)。ここで拾わないと FormBuilderPage の保存で null に戻ってしまう。
    capacity: typeof raw.capacity === 'number' ? raw.capacity : null,
    capacityFullMessage:
      typeof raw.capacityFullMessage === 'string' ? raw.capacityFullMessage : '',
    showRemainingCount: raw.showRemainingCount === true,
    relatedProductIds: Array.isArray(raw.relatedProductIds)
      ? (raw.relatedProductIds as string[])
      : [],
    // 営業ツール (Phase 1+)
    tag,
    dealStageHint,
    category,
    // 主軸カテゴリ (Phase 1+): primaryCategoryId が真のソース、primaryCategory は表示用スナップショット
    primaryCategoryId:
      typeof raw.primaryCategoryId === 'string' ? raw.primaryCategoryId : null,
    primaryCategory:
      typeof raw.primaryCategory === 'string' ? raw.primaryCategory : null,
    // 弊社紹介フラグ (v3.2.29+)
    isCompanyReferral: raw.isCompanyReferral === true,
    // SA ポータル表示可否 (v3.2.28+)。未設定 (undefined) は「表示」扱い
    visibleToSa: raw.visibleToSa === false ? false : true,
    // 自動返信で送付する資料 (Phase 8)
    documentId: typeof raw.documentId === 'string' ? raw.documentId : null,
    // 個別説明枠の表示対象 (?sa= 無しのときの部署フィルタ)。空配列は null = 全部署扱い。
    briefingDepartmentFilter: Array.isArray(raw.briefingDepartmentFilter)
      ? (raw.briefingDepartmentFilter as Form['briefingDepartmentFilter'])
      : null,
    // 完了通知（回答者本人への控えメール、2026-06-18）
    completionNotification: normalizeCompletionNotification(
      raw.completionNotification,
    ),
    createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : '',
    createdAt: raw.createdAt as Form['createdAt'],
    updatedAt: raw.updatedAt as Form['updatedAt'],
  };
}

// ─────────────────────────────────────────────
// list / get
// ─────────────────────────────────────────────
export function useForms() {
  return useQuery({
    queryKey: ['forms'],
    queryFn: async (): Promise<FormRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'forms'), orderBy('updatedAt', 'desc')),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...normalizeForm(d.data() as Record<string, unknown>),
      }));
    },
  });
}

export function useForm(id: string | null | undefined) {
  return useQuery({
    queryKey: ['forms', id],
    enabled: !!id,
    queryFn: async (): Promise<FormRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'forms', id));
      if (!snap.exists()) return null;
      return {
        id: snap.id,
        ...normalizeForm(snap.data() as Record<string, unknown>),
      };
    },
  });
}

// ─────────────────────────────────────────────
// CRUD
// ─────────────────────────────────────────────
/**
 * 新規作成・複製時の完了通知の既定値 (2026-07-28)。
 * enabled=true を既定にする（作成直後から回答者への控えメールが有効になる）。
 * 件名・本文が空でも、完了通知画面がフォーム内容から自動生成する。
 */
export const DEFAULT_COMPLETION_NOTIFICATION: NonNullable<
  Form['completionNotification']
> = {
  enabled: true,
  fromAccountId: null,
  cc: [],
  subject: '',
  body: '',
  url: null,
  includedFieldIds: null,
};

export function useCreateForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Form>): Promise<string> => {
      const ref = await safeAddDoc(collection(db, 'forms'), {
        name: data.name ?? '無題のフォーム',
        title: data.title ?? '無題のフォーム',
        description: data.description ?? '',
        elements: data.elements ?? [],
        thanksMessage: data.thanksMessage ?? 'ご回答ありがとうございました。',
        redirectUrl: data.redirectUrl ?? '',
        theme: data.theme ?? null,
        status: data.status ?? 'draft',
        publicStartAt: data.publicStartAt ?? null,
        publicEndAt: data.publicEndAt ?? null,
        campaignIds: data.campaignIds ?? [],
        contactTags: data.contactTags ?? [],
        submissionCount: 0,
        // 完了通知は既定 ON。複製元の設定があればそれを引き継ぎつつ enabled は必ず true。
        completionNotification: data.completionNotification
          ? { ...data.completionNotification, enabled: true }
          : DEFAULT_COMPLETION_NOTIFICATION,
        // 営業ツール (Phase 1+)
        ...(data.tag ? { tag: data.tag } : {}),
        ...(data.dealStageHint !== undefined
          ? { dealStageHint: data.dealStageHint }
          : {}),
        ...(data.category !== undefined ? { category: data.category } : {}),
        // 顧客情報の更新ポリシー。顧客情報確認・更新テンプレは 'overwrite' で作成する
        ...(data.contactOverwritePolicy
          ? { contactOverwritePolicy: data.contactOverwritePolicy }
          : {}),
        createdBy: data.createdBy ?? '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  });
}

export function useUpdateForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: Partial<Form> }) => {
      await safeUpdateDoc(doc(db, 'forms', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['forms'] });
      qc.invalidateQueries({ queryKey: ['forms', id] });
    },
  });
}

export function useDeleteForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'forms', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  });
}

// ─────────────────────────────────────────────
// 回収データ（Phase 24c）
// ─────────────────────────────────────────────
export function useFormSubmissions(formId?: string) {
  return useQuery({
    queryKey: ['form_submissions', formId ?? null],
    queryFn: async (): Promise<FormSubmissionRecord[]> => {
      const conditions = formId ? [where('formId', '==', formId)] : [];
      const snap = await getDocs(
        query(
          collection(db, 'form_submissions'),
          ...conditions,
          orderBy('submittedAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as FormSubmission),
      }));
    },
  });
}

// ─────────────────────────────────────────────
// 回収データへの返信メール (2026-07-23)
// 見積/請求メールと同じ prepare → send 構成の Callable を叩く。
// ─────────────────────────────────────────────
export interface PrepareFormReplyMailResult {
  defaultTo: string;
  fromAccountId: string | null;
  subject: string;
  body: string;
}

const prepareFormReplyMailFn = httpsCallable<
  { submissionId: string },
  PrepareFormReplyMailResult
>(functions, 'prepareFormReplyMail');

const sendFormReplyMailFn = httpsCallable<
  {
    submissionId: string;
    to: string;
    fromAccountId?: string | null;
    cc?: string[];
    subject?: string;
    body?: string;
  },
  { submissionId: string; mailSent: boolean; reason?: string }
>(functions, 'sendFormReplyMail');

export function usePrepareFormReplyMail() {
  return useMutation({
    mutationFn: async (input: { submissionId: string }) => {
      const r = await prepareFormReplyMailFn(input);
      return r.data;
    },
  });
}

export function useSendFormReplyMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Parameters<typeof sendFormReplyMailFn>[0] & { formId: string },
    ) => {
      const { formId: _formId, ...payload } = input;
      const r = await sendFormReplyMailFn(payload);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      // replySentAt が書き込まれ「返答待ち」→「返答済み」に変わるため再取得
      qc.invalidateQueries({ queryKey: ['form_submissions', vars.formId] });
    },
  });
}

// ─────────────────────────────────────────────
// 返答フラグの手動更新 (2026-08-07)
// form_submissions はクライアント update 禁止のため Callable 経由。
// ─────────────────────────────────────────────
const updateFormReplyStatusFn = httpsCallable<
  { submissionId: string; status: FormReplyStatusInput },
  { submissionId: string; status: FormReplyStatusInput }
>(functions, 'updateFormReplyStatus');

export function useUpdateFormReplyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      submissionId: string;
      status: FormReplyStatusInput;
      formId: string;
    }) => {
      const r = await updateFormReplyStatusFn({
        submissionId: input.submissionId,
        status: input.status,
      });
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['form_submissions', vars.formId] });
    },
  });
}

/**
 * 回収データ 1 件を削除し、forms.submissionCount を atomic に -1。
 * forms.submissionCount は denormalize なので併せて減算する。
 */
export function useDeleteFormSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; formId: string }) => {
      await safeDeleteDoc(doc(db, 'form_submissions', args.id));
      await safeUpdateDoc(doc(db, 'forms', args.formId), {
        submissionCount: increment(-1),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_, { formId }) => {
      qc.invalidateQueries({ queryKey: ['form_submissions', formId] });
      qc.invalidateQueries({ queryKey: ['forms'] });
      qc.invalidateQueries({ queryKey: ['forms', formId] });
    },
  });
}
